import { useState, type FormEvent } from 'react';

const FORM_ADDR = 'https://readdy.ai/api/form/daeltqjep053ijub1dr0';

type Status = 'idle' | 'ok' | 'err';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const trap = (fd.get('company_alt') as string) ?? '';
    if (trap.trim().length > 0) {
      form.reset();
      setStatus('ok');
      setFormError('');
      return;
    }

    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) {
      if (k === 'company_alt' && !String(v).trim()) continue;
      params.append(k, String(v));
    }

    setStatus('idle');
    setFormError('');
    try {
      const resp = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await resp.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = text ? JSON.parse(text) : {};
      } catch {
        parsed = {};
      }
      const msg = parsed?.meta?.message ?? parsed?.meta?.detail ?? text;
      if (resp.ok && parsed?.code === 'OK') {
        form.reset();
        setStatus('ok');
        setFormError('');
      } else {
        setStatus('err');
        setFormError(msg && msg.includes('spam') ? '提交未成功，请稍后重试。' : (msg || '提交未成功，请检查填写后重试。'));
      }
    } catch {
      setStatus('err');
      setFormError('网络异常，请稍后重试。');
    }
  }

  const fieldCls =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400';

  return (
    <form
      data-readdy-form={FORM_ADDR}
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label="读者联系表单"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            怎么称呼你
          </label>
          <input id="contact-name" name="name" type="text" required maxLength={30} placeholder="你的称呼" className={fieldCls} />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input id="contact-email" name="email" type="email" required maxLength={80} placeholder="you@example.com" className={fieldCls} />
        </div>
      </div>

      <div>
        <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
          想了解的内容
        </label>
        <select id="contact-topic" name="topic" className={fieldCls} defaultValue="事件复盘">
          <option>事件复盘</option>
          <option>反转梳理</option>
          <option>各方回应拆解</option>
          <option>后续进展追踪</option>
          <option>投稿与商务合作</option>
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          留言内容（最多 500 字）
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="告诉我们你想看的复盘，或对某一期的看法…"
          className={`${fieldCls} resize-none`}
        />
      </div>

      {/* 防垃圾蜜罐字段 */}
      <input
        type="text"
        name="company_alt"
        data-spamtrap
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        readOnly
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          提交留言
          <i className="ri-send-plane-line ml-2" aria-hidden="true" />
        </button>
        {status === 'ok' && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-accent-600">
            <i className="ri-checkbox-circle-line" aria-hidden="true" />
            已收到你的留言，我们会尽快回复。
          </p>
        )}
        {status === 'err' && (
          <p className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
            <i className="ri-error-warning-line" aria-hidden="true" />
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}